import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { GraduationCap, Clock, CheckSquare, BookOpen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useTheme } from '@/contexts/ThemeContext';
import DarkModeToggle from '@/components/DarkModeToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_11207ce1-f0ad-4af8-83b6-64fb331f35a6/artifacts/e6s5tkbt_OSSuccess6.png";



export default function CoursePreviewPage({ user, setUser, logout }) {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthMode, setShowAuthMode] = useState('signup'); // 'signup' or 'login'
  const [authLoading, setAuthLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Check if user has CE Membership (includes ce-membership or curbside-consult plans)
  const hasCEMembership = user && subscriptionStatus?.has_subscription && 
    (subscriptionStatus?.has_ce_membership || 
     subscriptionStatus?.active_plans?.includes('ce-membership') ||
     subscriptionStatus?.active_plans?.includes('curbside-consult'));
  
  // Theme-aware colors
  const colors = {
    pageBg: isDarkMode ? '#092036' : '#F4F4F4',
    headerBg: isDarkMode ? '#092036' : 'rgba(255,255,255,0.9)',
    headerBorder: isDarkMode ? 'transparent' : '#DADEEO',
    heading: isDarkMode ? '#ffffff' : '#092036',
    subtext: isDarkMode ? '#e2e8f0' : '#70767C',
    cardBg: isDarkMode ? '#0f2744' : 'white',
    inputBg: isDarkMode ? '#ffffff' : '#ffffff',
    inputText: isDarkMode ? '#092036' : '#092036',
    inputBorder: isDarkMode ? '#e2e8f0' : '#DADEEO',
    link: isDarkMode ? '#60a5fa' : '#102862',
    freeBadgeBg: isDarkMode ? '#065f46' : '#dcfce7',
    freeBadgeText: isDarkMode ? '#a7f3d0' : '#166534',
    memberBadgeBg: isDarkMode ? '#1e3a8a' : '#dbeafe',
    memberBadgeText: isDarkMode ? '#93c5fd' : '#1e40af',
  };
  
  // Auth form state
  const [authForm, setAuthForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    state: '',
    license_number: ''
  });

  useEffect(() => {
    fetchCourse();
    // Scroll to top when component mounts
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [courseId]);

  useEffect(() => {
    if (user) {
      fetchSubscriptionStatus();
    }
  }, [user]);

  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('session_token');
      if (!token) return;
      
      const response = await fetch(`${API}/subscriptions/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSubscriptionStatus(data);
      }
    } catch (error) {
      console.error('Error fetching subscription status:', error);
    }
  };

  const fetchCourse = async () => {
    try {
      // Fetch from courses-directory to get ALL published courses, not just featured ones
      const response = await fetch(`${API}/courses-directory`);
      const courses = await response.json();
      const foundCourse = courses.find(c => c.id === courseId);
      
      if (foundCourse) {
        setCourse(foundCourse);
      } else {
        toast.error('Course not found');
        navigate('/');
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      toast.error('Error loading course');
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setAuthLoading(true);

    try {
      const formData = new FormData();
      formData.append('email', authForm.email);
      formData.append('password', authForm.password);
      formData.append('name', `${authForm.firstName} ${authForm.lastName}`);
      formData.append('role', 'student');
      formData.append('state', authForm.state);
      formData.append('license_number', authForm.license_number);

      const response = await axios.post(`${API}/auth/register`, formData);
      
      // API returns session_token, not token
      const token = response.data.session_token || response.data.token;
      if (token) {
        localStorage.setItem('session_token', token);
        setUser(response.data.user);
        toast.success('Account created successfully!');
        
        // Auto-enroll and redirect to course
        enrollAndStartCourse(token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);

    try {
      const formData = new FormData();
      formData.append('email', authForm.email);
      formData.append('password', authForm.password);

      const response = await axios.post(`${API}/auth/login`, formData);
      
      // API returns session_token, not token
      const token = response.data.session_token || response.data.token;
      if (token) {
        localStorage.setItem('session_token', token);
        setUser(response.data.user);
        toast.success('Logged in successfully!');
        
        // Auto-enroll and redirect to course
        enrollAndStartCourse(token);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setAuthLoading(false);
    }
  };

  const enrollAndStartCourse = async (token) => {
    try {
      // For free courses, auto-enroll without payment
      if (!course.is_paid) {
        await axios.post(
          `${API}/courses/${courseId}/enroll`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        toast.success('Enrolled in course!');
        
        // Redirect to course page
        setTimeout(() => {
          navigate(`/course/${courseId}`);
        }, 500);
      } else {
        // For paid courses, would redirect to checkout
        toast.info('Redirecting to checkout...');
        navigate(`/course/${courseId}`);
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      // Still redirect to course page
      navigate(`/course/${courseId}`);
    }
  };

  const handleStartCourse = () => {
    if (user) {
      // User is logged in, enroll and start
      enrollAndStartCourse(localStorage.getItem('session_token'));
    } else {
      // Scroll to auth section
      document.getElementById('auth-section')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: isDarkMode ? '#092036' : '#F4F4F4' }}>
        <p style={{ color: isDarkMode ? '#e2e8f0' : '#70767C' }}>Loading course...</p>
      </div>
    );
  }

  if (!course) {
    return null;
  }

  return (
    <div className="min-h-screen grid-layout-page" style={{ background: colors.pageBg }}>
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isDarkMode ? '' : 'bg-white/90 backdrop-blur-md'} border-b`} style={{ backgroundColor: colors.headerBg, borderColor: colors.headerBorder }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer min-w-0 flex-1" onClick={() => navigate('/')}>
            <img src={LOGO_URL} alt="Oral Surgery Success" className="h-10 w-10 sm:h-12 sm:w-12 object-contain flex-shrink-0" style={isDarkMode ? { filter: 'drop-shadow(0 0 2px rgba(255,255,255,0.8))' } : {}} />
            <span className="text-base sm:text-2xl font-bold truncate" style={{ color: colors.heading }}>Oral Surgery Success</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <DarkModeToggle />
            {user ? (
              <>
                <Button
                  variant="outline"
                  onClick={() => navigate('/dashboard')}
                  className="text-xs sm:text-sm px-2 sm:px-4"
                  style={{ borderColor: isDarkMode ? '#60a5fa' : '#102862', color: isDarkMode ? '#60a5fa' : '#102862' }}
                >
                  <span className="hidden sm:inline">Dashboard</span>
                  <span className="sm:hidden">Home</span>
                </Button>
                <Button onClick={logout} variant="ghost" className="text-xs sm:text-sm px-2 sm:px-4" style={{ color: colors.subtext }}>
                  Logout
                </Button>
              </>
            ) : (
              <Button 
                onClick={() => navigate('/')}
                variant="ghost"
                className="text-xs sm:text-sm px-2 sm:px-4"
                style={{ color: colors.link }}
              >
                <span className="hidden sm:inline">← Back to Home</span>
                <span className="sm:hidden">Home</span>
              </Button>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pb-12 pt-6 sm:pt-8">
        {/* Course Hero - compact on mobile */}
        <div className="mb-6 sm:mb-12">
          {/* Course Image/Icon - show at top on mobile for better visual */}
          {course.icon_url && (
            <div className="mb-4 sm:mb-6">
              <img 
                src={course.icon_url.startsWith('http') ? course.icon_url : `${API}${course.icon_url}`}
                alt={course.title}
                className="w-full max-w-md mx-auto rounded-xl shadow-lg object-cover"
                style={{ maxHeight: '200px' }}
              />
            </div>
          )}
          
          {/* CE Hours and Badge - smaller on mobile */}
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-4">
            <Clock className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: '#FF6B35' }} />
            <span className="text-sm sm:text-lg font-semibold" style={{ color: '#FF6B35' }}>
              {course.ce_hours} CE {course.ce_hours === 1 ? 'Hour' : 'Hours'}
            </span>
            {course.is_paid ? (
              <span className="text-xs sm:text-sm font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded" style={{ background: colors.memberBadgeBg, color: colors.memberBadgeText }}>
                MEMBERS ONLY
              </span>
            ) : (
              <span className="text-xs sm:text-sm font-semibold px-2 sm:px-3 py-0.5 sm:py-1 rounded" style={{ background: colors.freeBadgeBg, color: colors.freeBadgeText }}>
                FREE
              </span>
            )}
          </div>
          
          {/* Course Title - smaller on mobile */}
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-4" style={{ color: colors.heading }}>
            {course.title}
          </h1>
          
          {/* Description - smaller on mobile */}
          <p className="text-sm sm:text-lg mb-4 sm:mb-6" style={{ color: colors.subtext }}>
            {course.description}
          </p>

          {/* CTA Buttons - different based on login status and membership */}
          <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
            {user ? (
              // Logged-in user CTAs
              (!course.is_paid || hasCEMembership) ? (
                <>
                  <Button
                    onClick={handleStartCourse}
                    className="text-white border-0 py-3 px-4 sm:py-6 sm:px-8 text-sm sm:text-lg"
                    style={{ background: !course.is_paid ? '#16a34a' : '#FF6B35' }}
                  >
                    {!course.is_paid ? 'Start Free Course' : 'Start Course'}
                  </Button>
                  {course.is_paid && hasCEMembership && (
                    <span className="text-xs sm:text-sm" style={{ color: colors.subtext }}>
                      Included with CE Membership
                    </span>
                  )}
                </>
              ) : (
                /* Logged-in non-member on paid course */
                <Button
                  onClick={() => navigate('/checkout?plan=ce-membership')}
                  className="text-white border-0 py-3 px-4 sm:py-6 sm:px-8 text-sm sm:text-lg"
                  style={{ background: '#102862' }}
                >
                  Unlock with Membership
                </Button>
              )
            ) : (
              // Non-logged-in user CTAs
              !course.is_paid ? (
                <Button
                  onClick={() => navigate(`/auth?redirect=/course-preview/${courseId}&action=start`)}
                  className="text-white border-0 py-3 px-4 sm:py-6 sm:px-8 text-sm sm:text-lg"
                  style={{ background: '#16a34a' }}
                >
                  Start Free Course
                </Button>
              ) : (
                <Button
                  onClick={() => navigate('/checkout?plan=ce-membership')}
                  className="text-white border-0 py-3 px-4 sm:py-6 sm:px-8 text-sm sm:text-lg"
                  style={{ background: '#102862' }}
                >
                  Unlock with Membership
                </Button>
              )
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main Content - 2 columns */}
          <div className="md:col-span-2 space-y-8">
            {/* About this course */}
            <Card className="p-6" style={{ backgroundColor: colors.cardBg }}>
              <h2 className="text-2xl font-bold mb-4" style={{ color: colors.heading }}>
                About This Course
              </h2>
              <div className="space-y-4" style={{ color: colors.subtext }}>
                <p>{course.description}</p>
                <p>
                  Upon completion, you&apos;ll receive a certificate for {course.ce_hours} CE {course.ce_hours === 1 ? 'hour' : 'hours'} that can be used toward your continuing education requirements.
                </p>
              </div>
            </Card>

            {/* Course Details Card */}
            <Card className="p-6" style={{ backgroundColor: colors.cardBg }}>
              <h3 className="text-lg font-bold mb-4" style={{ color: colors.heading }}>
                Course Details
              </h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5" style={{ color: '#FF6B35' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: colors.heading }}>CE Hours</p>
                    <p className="text-sm" style={{ color: colors.subtext }}>{course.ce_hours} {course.ce_hours === 1 ? 'Hour' : 'Hours'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <BookOpen className="w-5 h-5" style={{ color: isDarkMode ? '#60a5fa' : '#102862' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: colors.heading }}>Format</p>
                    <p className="text-sm" style={{ color: colors.subtext }}>Self-paced online course</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <GraduationCap className="w-5 h-5" style={{ color: '#16A34A' }} />
                  <div>
                    <p className="text-sm font-semibold" style={{ color: colors.heading }}>Certificate</p>
                    <p className="text-sm" style={{ color: colors.subtext }}>Issued upon completion</p>
                  </div>
                </div>
              </div>
            </Card>

            {/* What You&apos;ll Learn */}
            {course.learning_outcomes && course.learning_outcomes.length > 0 && (
              <Card className="p-6" style={{ backgroundColor: colors.cardBg }}>
                <h2 className="text-2xl font-bold mb-4" style={{ color: colors.heading }}>
                  What You&apos;ll Learn
                </h2>
                <ul className="space-y-3">
                  {course.learning_outcomes.map((outcome, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckSquare className="w-5 h-5 mt-1 flex-shrink-0" style={{ color: '#16A34A' }} />
                      <span style={{ color: colors.subtext }}>{outcome}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {/* Requirements */}
            {course.requirements && (
              <Card className="p-6" style={{ backgroundColor: colors.cardBg }}>
                <h2 className="text-2xl font-bold mb-4" style={{ color: colors.heading }}>
                  Requirements
                </h2>
                <p style={{ color: colors.subtext }}>{course.requirements}</p>
              </Card>
            )}
          </div>

          {/* Sidebar - 1 column */}
          <div className="md:col-span-1">
            {/* Auth Card - Only show if not logged in */}
            {!user && (
              <Card className="p-6 sticky top-24 light-form-card" id="auth-section" style={{ backgroundColor: '#ffffff' }}>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#092036' }}>
                  {showAuthMode === 'signup' ? 'Create Your Free Account' : 'Welcome Back'}
                </h3>
                <p className="text-sm mb-6" style={{ color: '#70767C' }}>
                  {showAuthMode === 'signup' 
                    ? 'Sign up to start this course and track your CE hours'
                    : 'Log in to continue your learning journey'}
                </p>

                <form onSubmit={showAuthMode === 'signup' ? handleSignup : handleLogin} className="space-y-4">
                  {showAuthMode === 'signup' && (
                    <>
                      <div>
                        <Label htmlFor="firstName" style={{ color: '#092036' }}>First Name <span className="text-red-500">*</span></Label>
                        <input
                          id="firstName"
                          type="text"
                          value={authForm.firstName}
                          onChange={(e) => setAuthForm({ ...authForm, firstName: e.target.value })}
                          required
                          className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                          style={{ backgroundColor: '#ffffff', color: '#092036', borderColor: '#DADEEO' }}
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName" style={{ color: '#092036' }}>Last Name <span className="text-red-500">*</span></Label>
                        <input
                          id="lastName"
                          type="text"
                          value={authForm.lastName}
                          onChange={(e) => setAuthForm({ ...authForm, lastName: e.target.value })}
                          required
                          className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                          style={{ backgroundColor: '#ffffff', color: '#092036', borderColor: '#DADEEO' }}
                        />
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="state" style={{ color: '#092036' }}>State <span className="text-red-500">*</span></Label>
                          <select
                            id="state"
                            value={authForm.state}
                            onChange={(e) => setAuthForm({ ...authForm, state: e.target.value })}
                            required
                            className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                            style={{ backgroundColor: '#ffffff', color: '#092036', borderColor: '#DADEEO' }}
                          >
                            <option value="">Select</option>
                            <option value="AL">AL</option>
                            <option value="AK">AK</option>
                            <option value="AZ">AZ</option>
                            <option value="AR">AR</option>
                            <option value="CA">CA</option>
                            <option value="CO">CO</option>
                            <option value="CT">CT</option>
                            <option value="DE">DE</option>
                            <option value="FL">FL</option>
                            <option value="GA">GA</option>
                            <option value="HI">HI</option>
                            <option value="ID">ID</option>
                            <option value="IL">IL</option>
                            <option value="IN">IN</option>
                            <option value="IA">IA</option>
                            <option value="KS">KS</option>
                            <option value="KY">KY</option>
                            <option value="LA">LA</option>
                            <option value="ME">ME</option>
                            <option value="MD">MD</option>
                            <option value="MA">MA</option>
                            <option value="MI">MI</option>
                            <option value="MN">MN</option>
                            <option value="MS">MS</option>
                            <option value="MO">MO</option>
                            <option value="MT">MT</option>
                            <option value="NE">NE</option>
                            <option value="NV">NV</option>
                            <option value="NH">NH</option>
                            <option value="NJ">NJ</option>
                            <option value="NM">NM</option>
                            <option value="NY">NY</option>
                            <option value="NC">NC</option>
                            <option value="ND">ND</option>
                            <option value="OH">OH</option>
                            <option value="OK">OK</option>
                            <option value="OR">OR</option>
                            <option value="PA">PA</option>
                            <option value="RI">RI</option>
                            <option value="SC">SC</option>
                            <option value="SD">SD</option>
                            <option value="TN">TN</option>
                            <option value="TX">TX</option>
                            <option value="UT">UT</option>
                            <option value="VT">VT</option>
                            <option value="VA">VA</option>
                            <option value="WA">WA</option>
                            <option value="WV">WV</option>
                            <option value="WI">WI</option>
                            <option value="WY">WY</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="license_number" style={{ color: '#092036' }}>License # <span className="text-red-500">*</span></Label>
                          <input
                            id="license_number"
                            type="text"
                            placeholder="12345"
                            value={authForm.license_number}
                            onChange={(e) => setAuthForm({ ...authForm, license_number: e.target.value })}
                            required
                            className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                            style={{ backgroundColor: '#ffffff', color: '#092036', borderColor: '#DADEEO' }}
                          />
                        </div>
                      </div>
                    </>
                  )}
                  
                  <div>
                    <Label htmlFor="email" style={{ color: '#092036' }}>Email</Label>
                    <input
                      id="email"
                      type="email"
                      value={authForm.email}
                      onChange={(e) => setAuthForm({ ...authForm, email: e.target.value })}
                      required
                      className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                      style={{ backgroundColor: '#ffffff', color: '#092036', borderColor: '#DADEEO' }}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="password" style={{ color: '#092036' }}>Password</Label>
                    <input
                      id="password"
                      type="password"
                      value={authForm.password}
                      onChange={(e) => setAuthForm({ ...authForm, password: e.target.value })}
                      required
                      className="flex h-10 w-full rounded-md border px-3 py-2 text-sm"
                      style={{ backgroundColor: '#ffffff', color: '#092036', borderColor: '#DADEEO' }}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={authLoading}
                    className="w-full text-white border-0"
                    style={{ background: '#102862' }}
                  >
                    {authLoading ? 'Processing...' : (showAuthMode === 'signup' ? 'Create Account & Start Course' : 'Log In & Start Course')}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => setShowAuthMode(showAuthMode === 'signup' ? 'login' : 'signup')}
                    className="text-sm hover:underline"
                    style={{ color: '#102862' }}
                  >
                    {showAuthMode === 'signup' ? 'Already have an account? Log in' : "Don't have an account? Sign up"}
                  </button>
                </div>

                <p className="text-xs mt-4 text-center" style={{ color: '#70767C' }}>
                  We track CE hours and issue certificates upon course completion
                </p>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
